import * as React from 'react';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import visuallyHidden from '@mui/utils/visuallyHidden';
import { useStore } from '@base-ui/utils/store';
import { useId } from '@base-ui/utils/useId';
import { isReactVersionAtLeast } from '@base-ui/utils/reactVersion';
import RepeatRounded from '@mui/icons-material/RepeatRounded';
import { TimelineGrid } from '@mui/x-scheduler-internals-premium/timeline-grid';
import {
  schedulerEventSelectors,
  schedulerResourceSelectors,
} from '@mui/x-scheduler-internals/scheduler-selectors';
import { getOccurrenceDataTimezone } from '@mui/x-scheduler-internals/internals';
import { eventTimelinePremiumDependencySelectors } from '@mui/x-scheduler-internals-premium/event-timeline-premium-selectors';
import type { SchedulerDependencyType } from '@mui/x-scheduler-internals-premium/models';
import { useEventTimelinePremiumStoreContext } from '@mui/x-scheduler-internals-premium/use-event-timeline-premium-store-context';
import {
  EventDragPreview,
  getPaletteVariants,
  useEventAccessibleName,
  useSchedulerSlots,
} from '@mui/x-scheduler/internals';
import type {
  EventTimelinePremiumSlots,
  EventTimelinePremiumSlotProps,
} from '../../../models/slots';
import type { EventTimelinePremiumEventProps } from './EventTimelinePremiumEvent.types';
import { useEventTimelinePremiumStyledContext } from '../../EventTimelinePremiumStyledContext';
import { eventTimelinePremiumClasses } from '../../eventTimelinePremiumClasses';

// React 18 drops `inert={true}` as an unknown boolean attribute and React 19 drops `inert=""`.
const INERT_PROPS = (isReactVersionAtLeast(19) ? { inert: true } : { inert: '' }) as {
  inert?: boolean;
};

const ARROW_DEPTH = 8; // px - depth of the chevron point
const LEFT_ARROW_CLIP = `polygon(${ARROW_DEPTH}px 0, 100% 0, 100% 100%, ${ARROW_DEPTH}px 100%, 0 50%)`;
const RIGHT_ARROW_CLIP = `polygon(0 0, calc(100% - ${ARROW_DEPTH}px) 0, 100% 50%, calc(100% - ${ARROW_DEPTH}px) 100%, 0 100%)`;
const BOTH_ARROWS_CLIP = `polygon(${ARROW_DEPTH}px 0, calc(100% - ${ARROW_DEPTH}px) 0, 100% 50%, calc(100% - ${ARROW_DEPTH}px) 100%, ${ARROW_DEPTH}px 100%, 0 50%)`;

const EventTimelinePremiumEventRoot = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'Event',
})(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'var(--event-surface-subtle)',
  color: 'var(--event-on-surface-subtle-primary)',
  padding: theme.spacing(0.5, 1),
  position: 'relative',
  width: 'var(--width)',
  height: `calc(${theme.typography.body2.lineHeight}em + ${theme.spacing(1)})`,
  marginLeft: 'var(--x-position)',
  gridRow: 'var(--row-index, 1)',
  gridColumn: 1,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  cursor: 'pointer',
  '&[data-dragging], &[data-resizing]': {
    opacity: 0.5,
  },
  '&:hover': {
    backgroundColor: 'var(--event-surface-subtle-hover)',
  },
  '&[data-editing]': {
    backgroundColor: 'var(--event-surface-selected)',
    color: 'var(--event-on-surface-selected)',
    '&:hover': {
      backgroundColor: 'var(--event-surface-selected-hover)',
    },
    '&::before': {
      background: 'var(--event-surface-selected)',
    },
  },
  [`&:hover .${eventTimelinePremiumClasses.eventResizeHandler}`]: {
    opacity: 1,
  },
  '&[data-dependency-drop-target]': {
    outline: '2px solid var(--event-surface-accent)',
    outlineOffset: 1,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: 3,
    borderRadius: '4px 0 0 4px',
    background: 'var(--event-surface-accent)',
    pointerEvents: 'none',
  },
  '&[data-starting-before-edge]': {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    clipPath: LEFT_ARROW_CLIP,
    paddingLeft: ARROW_DEPTH + 8,
    '&::before': {
      display: 'none',
    },
  },
  '&[data-ending-after-edge]': {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    clipPath: RIGHT_ARROW_CLIP,
    paddingRight: ARROW_DEPTH + 8,
  },
  '&[data-starting-before-edge][data-ending-after-edge]': {
    clipPath: BOTH_ARROWS_CLIP,
  },
  variants: getPaletteVariants(theme),
}));

const EventTimelinePremiumEventLinesClamp = styled('span', {
  name: 'MuiEventTimeline',
  slot: 'EventLinesClamp',
})({
  display: '-webkit-box',
  WebkitLineClamp: 'var(--number-of-lines)',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
});

const EventTimelinePremiumEventRecurringIcon = styled(RepeatRounded, {
  name: 'MuiEventTimeline',
  slot: 'EventRecurringIcon',
})({
  flexShrink: 0,
  fontSize: '1rem',
});

const EventTimelinePremiumEventResizeHandler = styled(TimelineGrid.EventResizeHandler, {
  name: 'MuiEventTimeline',
  slot: 'EventResizeHandler',
})({
  position: 'absolute',
  width: 4,
  top: 0,
  bottom: 0,
  zIndex: 3,
  cursor: 'ew-resize',
  opacity: 0,
  '&[data-start]': {
    left: 0,
  },
  '&[data-end]': {
    right: 0,
  },
});

// TODO(dependencies public flip, #23420): move to localeText. Hardcoded while the feature has
// no public API.
const DEPENDENCY_SOURCE_DESCRIPTIONS: Record<SchedulerDependencyType, (title: string) => string> = {
  FinishToStart: (title) => `Cannot start until ${title} finishes.`,
  StartToStart: (title) => `Cannot start until ${title} starts.`,
  FinishToFinish: (title) => `Cannot finish until ${title} finishes.`,
  StartToFinish: (title) => `Cannot finish until ${title} starts.`,
};

export const EventTimelinePremiumEvent = React.forwardRef(function EventTimelinePremiumEvent(
  props: EventTimelinePremiumEventProps,
  forwardedRef: React.ForwardedRef<HTMLDivElement>,
) {
  const {
    occurrence,
    className,
    variant,
    id: idProp,
    style,
    resourceId,
    elementPosition,
    ...other
  } = props;

  // Context hooks
  const store = useEventTimelinePremiumStoreContext();
  const { classes, localeText, getEventAriaLabel } = useEventTimelinePremiumStyledContext();
  const { slots, slotProps } = useSchedulerSlots<
    EventTimelinePremiumSlots,
    EventTimelinePremiumSlotProps
  >();
  // Selector hooks
  const isDraggable = useStore(store, schedulerEventSelectors.isDraggable, occurrence.id);
  const isStartResizable = useStore(
    store,
    schedulerEventSelectors.isResizable,
    occurrence.id,
    'start',
  );
  const isEndResizable = useStore(store, schedulerEventSelectors.isResizable, occurrence.id, 'end');
  const color = useStore(store, schedulerEventSelectors.color, occurrence.id, resourceId);
  const isRecurring = useStore(store, schedulerEventSelectors.isRecurring, occurrence.id);
  const dependencySources = useStore(
    store,
    eventTimelinePremiumDependencySelectors.activeSourcesForTarget,
    occurrence.id,
  );
  const rowResource = useStore(store, schedulerResourceSelectors.processedResource, resourceId);

  // Feature hooks
  const id = useId(idProp);
  const defaultAccessibleName = useEventAccessibleName({
    occurrence,
    isRecurring,
    resourceName: rowResource?.title ?? null,
    localeText,
  });
  const accessibleName = getEventAriaLabel
    ? getEventAriaLabel({
        occurrence,
        resource: rowResource!,
        defaultAriaLabel: defaultAccessibleName,
      })
    : defaultAccessibleName;

  const EventContent = slots.timelineEventContent;
  const content = EventContent ? (
    <EventContent
      occurrence={occurrence}
      resource={rowResource!}
      variant={variant}
      {...slotProps.timelineEventContent}
    />
  ) : (
    occurrence.title
  );

  const sharedProps = {
    id,
    start: occurrence.displayTimezone.start,
    end: occurrence.displayTimezone.end,
    ref: forwardedRef,
    className: clsx(className, occurrence.className),
    style: {
      '--number-of-lines': 1,
      '--row-index': occurrence.position.firstIndex,
    } as React.CSSProperties,
    'data-palette': color,
    ...other,
  };

  if (variant === 'placeholder') {
    return (
      <TimelineGrid.EventPlaceholder
        render={<EventTimelinePremiumEventRoot />}
        aria-hidden={true}
        // The slot content is a preview here, so it must not take the focus.
        {...INERT_PROPS}
        {...sharedProps}
        className={clsx(sharedProps.className, classes.eventPlaceholder)}
      >
        <EventTimelinePremiumEventLinesClamp className={classes.eventLinesClamp}>
          {content}
        </EventTimelinePremiumEventLinesClamp>
        {isRecurring && (
          <EventTimelinePremiumEventRecurringIcon
            className={classes.eventRecurringIcon}
            aria-hidden="true"
          />
        )}
      </TimelineGrid.EventPlaceholder>
    );
  }

  return (
    <TimelineGrid.Event
      render={<EventTimelinePremiumEventRoot />}
      isDraggable={isDraggable}
      eventId={occurrence.id}
      occurrenceKey={occurrence.key}
      dataTimezone={getOccurrenceDataTimezone(occurrence)}
      elementPosition={elementPosition}
      renderDragPreview={(parameters) => <EventDragPreview {...parameters} />}
      aria-label={accessibleName}
      {...sharedProps}
      aria-describedby={dependencySources.length > 0 ? `${id}-dependencies` : undefined}
      className={clsx(sharedProps.className, classes.event)}
    >
      {isStartResizable && (
        <EventTimelinePremiumEventResizeHandler
          side="start"
          className={classes.eventResizeHandler}
        />
      )}
      <EventTimelinePremiumEventLinesClamp className={classes.eventLinesClamp}>
        {content}
      </EventTimelinePremiumEventLinesClamp>
      {dependencySources.length > 0 && (
        // Visually hidden, and `aria-hidden` so it is announced through the `aria-describedby`
        // reference above rather than as a child of the event.
        <span id={`${id}-dependencies`} style={visuallyHidden} aria-hidden>
          {dependencySources
            .map((source) => DEPENDENCY_SOURCE_DESCRIPTIONS[source.type](source.title))
            .join(' ')}
        </span>
      )}
      {isRecurring && (
        <EventTimelinePremiumEventRecurringIcon
          className={classes.eventRecurringIcon}
          aria-hidden="true"
        />
      )}
      {isEndResizable && (
        <EventTimelinePremiumEventResizeHandler side="end" className={classes.eventResizeHandler} />
      )}
    </TimelineGrid.Event>
  );
});
